import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, X, Clock, ImageOff, ArrowRight } from 'lucide-react';
import * as searchApi from '../api/endpoints/search';
// Device-local search history (cleared on sign-out by AuthContext). The
// catalog search itself is public (DE-14178), so this is not auth-gated.
import { readRecentSearches, saveRecentSearch } from '../utils/recentSearches';
import { focusRing } from '../styles/ui';

const MAX_VISIBLE_RESULTS = 6;
const MIN_QUERY_LENGTH = 1;
const DEBOUNCE_MS = 250;

// Ranks and highlights are computed client-side because the fields the
// design needs (category, price) only exist on the full /search endpoint's
// product objects, not the lightweight /search/suggestions payload. The
// backend already does its own name-based relevance ordering for the
// `products` it returns; this re-sorts by the requested priority tiers
// (exact name > name-starts-with > category > tag > description) since the
// backend doesn't expose that distinction itself.
const rankProducts = (products, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return products;

  const scoreOf = (product) => {
    const name = (product.displayName || '').toLowerCase();
    const category = (product.categoryLabel || product.category || '').toLowerCase();
    const tags = Array.isArray(product.tags) ? product.tags.join(' ').toLowerCase() : '';
    const description = (product.shortDescription || '').toLowerCase();

    if (name === q) return 5;
    if (name.startsWith(q)) return 4;
    if (name.includes(q)) return 3;
    if (category.includes(q)) return 2;
    if (tags.includes(q)) return 1.5;
    if (description.includes(q)) return 1;
    return 0;
  };

  return [...products].sort((a, b) => scoreOf(b) - scoreOf(a));
};

// Wraps the substring of `text` that case-insensitively matches `query` in
// a <mark> so the dropdown can visually highlight what the user typed.
const HighlightedText = ({ text, query }) => {
  if (!query.trim()) return <>{text}</>;
  const index = text.toLowerCase().indexOf(query.trim().toLowerCase());
  if (index === -1) return <>{text}</>;
  const before = text.slice(0, index);
  const match = text.slice(index, index + query.trim().length);
  const after = text.slice(index + query.trim().length);
  return (
    <>
      {before}
      <mark className="bg-highlight-200/70 text-emerald-950 rounded-sm px-0.5">{match}</mark>
      {after}
    </>
  );
};

const NavSearch = ({ variant = 'desktop', onRequestClose }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';
  const [query, setQuery] = useState(queryParam);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState(readRecentSearches);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const showRecent = isOpen && !query.trim() && recentSearches.length > 0;
  // Every other dropdown branch (sign-in prompt, loading, error, no-results,
  // results list) is gated on a non-empty query, so this is the single
  // source of truth for "is there actually something to render" — used to
  // keep the panel from mounting as an empty box (e.g. after clearQuery()
  // while isOpen is still true from an earlier focus/keystroke).
  const hasDropdownContent = showRecent || query.trim().length > 0;
  const visibleResults = results.slice(0, MAX_VISIBLE_RESULTS);

  const runSearch = useCallback(
    (term) => {
      const trimmed = term.trim();
      // DE-14178: search is public — guests get the same catalog search as
      // signed-in shoppers, so there is deliberately no auth check here.
      if (trimmed.length < MIN_QUERY_LENGTH) {
        setStatus('idle');
        setResults([]);
        return;
      }
      const requestId = ++requestIdRef.current;
      setStatus('loading');
      searchApi
        .search(trimmed, 20)
        .then((response) => {
          if (requestId !== requestIdRef.current) return; // stale response, a newer keystroke superseded it
          const products = response.data?.products || [];
          setResults(rankProducts(products, trimmed));
          setStatus('success');
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setStatus('error');
          setResults([]);
        });
    },
    []
  );

  // Keep the box in step with ?q= when something other than this component
  // changes it — Back/Forward, or landing on /search?q=… — since NavSearch
  // stays mounted in the header across those navigations.
  useEffect(() => {
    setQuery(queryParam);
  }, [queryParam]);

  useEffect(() => {
    // SearchResultsPage owns loading /search?q=... routes. Only fetch header
    // suggestions after the shopper has opened this autocomplete.
    if (!isOpen) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [isOpen, query, runSearch]);

  // Closes the dropdown on outside click, matching the Escape-to-close
  // pattern already used by the account menu and mobile nav in Navbar.jsx.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const closeDropdown = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const goToProduct = (product) => {
    saveRecentSearch(query);
    setRecentSearches(readRecentSearches());
    closeDropdown();
    onRequestClose?.();
    navigate(`/products/${product.sku || product._id}`);
  };

  const goToAllResults = (term = query) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    closeDropdown();
    onRequestClose?.();
    // DE-14178: the results page is public, so a guest goes straight there
    // rather than being diverted through sign-in.
    saveRecentSearch(trimmed);
    setRecentSearches(readRecentSearches());
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeDropdown();
      inputRef.current?.blur();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0 && visibleResults[activeIndex]) {
        goToProduct(visibleResults[activeIndex]);
      } else if (showRecent && activeIndex >= 0 && recentSearches[activeIndex]) {
        setQuery(recentSearches[activeIndex]);
        runSearch(recentSearches[activeIndex]);
      } else {
        goToAllResults();
      }
      return;
    }
    const list = showRecent ? recentSearches : visibleResults;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (list.length === 0) return;
      setActiveIndex((prev) => (prev + 1) % list.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (list.length === 0) return;
      setActiveIndex((prev) => (prev <= 0 ? list.length - 1 : prev - 1));
    }
  };

  const clearQuery = () => {
    setQuery('');
    setResults([]);
    setStatus('idle');
    inputRef.current?.focus();
  };

  const listboxId = 'nav-search-listbox';
  const activeDescendantId =
    activeIndex >= 0 ? `nav-search-option-${activeIndex}` : undefined;

  const containerWidthClass = variant === 'mobile' ? 'w-full' : 'w-full max-w-md';

  // The panel is deliberately NOT sized to the input. In the desktop navbar
  // the input is a `flex-1 min-w-0` item competing with the logo, the full
  // link row and the action cluster, so it collapses to ~160px at wide
  // viewports — and a panel stretched to `left-0 right-0` inherited exactly
  // that, truncating every product name. It gets its own comfortable measure
  // instead: never narrower than the input (min-w-full), never wider than the
  // viewport (max-w). The mobile bar is already full-bleed, so it just fills.
  const panelWidthClass =
    variant === 'mobile' ? 'w-full' : 'min-w-full w-[26rem] max-w-[calc(100vw-2rem)]';

  return (
    <div ref={containerRef} className={`relative ${containerWidthClass}`}>
      <div
        className={`flex items-center gap-2 bg-white rounded-full border border-emerald-100 shadow-soft px-4 h-11 sm:h-12 transition-all duration-200 focus-within:shadow-card focus-within:border-emerald-300 ${focusRing}`}
      >
        <SearchIcon
          size={18}
          className="text-gray-400 shrink-0 transition-colors duration-200"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          // type="text" (not "search") — Chromium renders its own native
          // clear "x" for type="search" inputs with a value, which doubled
          // up with the custom clear button below it.
          type="text"
          role="combobox"
          aria-expanded={isOpen && hasDropdownContent}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendantId}
          aria-label="Search for products"
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => {
            // Only opens if there's something to show — a query already
            // typed, or recent searches to list. An empty box with no
            // history has nothing to render, so opening it would just show
            // an empty panel.
            if (query.trim() || recentSearches.length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search for Chicken, Mutton, Fish, Eggs..."
          className="flex-1 min-w-0 outline-none text-sm text-ink-primary placeholder:text-gray-400 bg-transparent"
        />
        {query && (
          <button
            type="button"
            onClick={clearQuery}
            aria-label="Clear search"
            className={`shrink-0 text-gray-400 hover:text-gray-600 transition-colors ${focusRing}`}
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {isOpen && hasDropdownContent && (
        <div
          id={listboxId}
          role="listbox"
          className={`absolute left-0 top-full mt-2 ${panelWidthClass} bg-white rounded-2xl shadow-floating border border-emerald-50 overflow-hidden z-50 animate-fade-in`}
          style={{ animationDuration: '200ms' }}
        >
          {showRecent && (
            <div className="py-2">
              <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Recent searches
              </p>
              {recentSearches.map((term, index) => (
                <button
                  key={term}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  id={`nav-search-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={activeIndex === index}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => goToAllResults(term)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                    activeIndex === index ? 'bg-emerald-50 text-emerald-800' : 'text-gray-700 hover:bg-emerald-50'
                  }`}
                >
                  <Clock size={14} className="text-gray-400 shrink-0" aria-hidden="true" />
                  {term}
                </button>
              ))}
            </div>
          )}

          {!showRecent && query.trim() && status === 'loading' && (
            <div className="p-6 text-center text-sm text-ink-secondary">Searching…</div>
          )}

          {!showRecent && query.trim() && status === 'error' && (
            <div className="p-6 text-center text-sm text-red-600">Something went wrong. Please try again.</div>
          )}

          {!showRecent && query.trim() && status === 'success' && results.length === 0 && (
            <div className="p-6 text-center">
              <p className="text-sm font-medium text-ink-primary mb-2">🔍 No products found</p>
              <p className="text-xs text-ink-secondary mb-2">Try searching for:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {['Chicken', 'Mutton', 'Fish', 'Eggs'].map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      setQuery(suggestion);
                      runSearch(suggestion);
                    }}
                    className={`text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full hover:bg-emerald-100 transition-colors ${focusRing}`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!showRecent && query.trim() && status === 'success' && results.length > 0 && (
            <>
              {/* ~5 rows (72px each) before scrolling, so a 6th hints that
                  there is more without the list dominating the viewport. */}
              <ul className="nav-search-scroll max-h-[360px] overflow-y-auto py-2">
                {visibleResults.map((product, index) => (
                  <li key={product._id}>
                    <button
                      ref={(node) => {
                        optionRefs.current[index] = node;
                      }}
                      id={`nav-search-option-${index}`}
                      type="button"
                      role="option"
                      aria-selected={activeIndex === index}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => goToProduct(product)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-150 ${
                        activeIndex === index ? 'bg-emerald-50' : 'hover:bg-emerald-50'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-emerald-50 overflow-hidden shrink-0 flex items-center justify-center">
                        {product.thumbnail ? (
                          <img
                            src={product.thumbnail}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageOff size={18} className="text-emerald-300" aria-hidden="true" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-ink-primary leading-snug line-clamp-2">
                          <HighlightedText text={product.displayName} query={query} />
                        </p>
                        <p className="text-xs text-ink-secondary truncate mt-0.5">
                          {product.categoryLabel || product.category || 'Product'}
                        </p>
                      </div>
                      {product.price != null && (
                        <div className="shrink-0 pl-2 text-right">
                          <p className="text-sm font-bold text-emerald-800 leading-none">₹{product.price}</p>
                          {product.mrp != null && product.mrp > product.price && (
                            <p className="text-[11px] text-gray-400 line-through mt-1 leading-none">₹{product.mrp}</p>
                          )}
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {/* Sibling of the scroll area, so it stays put while the list
                  scrolls. Shown whenever there are results — reaching the full
                  results page is useful even when nothing is hidden. */}
              <button
                type="button"
                onClick={() => goToAllResults()}
                className={`w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700 py-3.5 border-t border-emerald-50 hover:bg-emerald-50 transition-colors ${focusRing}`}
              >
                View All Results ({results.length})
                <ArrowRight size={15} aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NavSearch;
